// Energy-Summary-Store: aggregiert die Watt-Werte aller meldenden
// Steckdosen + Schalter, damit die Dashboard-Karte einen Live-Total
// anzeigen kann.
//
// Datenfluss:
//   - Die Geräte (Zigbee und Z-Wave mit Leistung/Zählerstand) kommen aus dem
//     gemeinsamen Steckdosen-Stand (lib/steckdosen.svelte.ts), den das
//     Homelab-Protokoll (Topic steckdose.stand, docs/protokoll.md ›
//     „Steckdosen über das Protokoll“) speist -- kein eigener Abruf, kein
//     5-s-Takt, kein Abo auf den alten WS-Kanal "energy" mehr. total_watts und
//     reporting rechnet dieser Store aus den Geräten.
//   - spark (Verlauf, GET /v2-api/energy/history) und totals (kWh heute/Monat/
//     Jahr, GET /v2-api/energy/totals) sind Aggregate aus der Datenbank:
//     totals im 60-s-Takt, spark alle 5 min (30-min-Buckets).
//
// SWR: ja -- spark/totals kommen beim Reload sofort aus der Ablage, die Geräte
// aus der Ablage des Steckdosen-Stands.

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { getEnergieGeraete, startSteckdosen } from "./steckdosen.svelte";
import type { EnergyDevice } from "./types";

const STORAGE_KEY = "energy";

export interface EnergySummary {
  devices: EnergyDevice[];
  total_watts: number;
  reporting: number; // Anzahl Geraete mit watts != null
  // 48-h-Verlauf des Total-Watts fuer die Spark-Line auf der Dashboard-
  // Karte. Backend liefert 30-min-Buckets; null-Buckets bleiben null,
  // damit SparkLine sie ueberspringen kann statt eine Linie auf 0 zu
  // ziehen.
  spark?: { values: (number | null)[] };
  // kWh-Aggregat fuer heute / Monat / Jahr (lokale Server-TZ). Die
  // EUR-Felder sind nur gesetzt, wenn ein Strompreis konfiguriert ist
  // (Settings.electricity_price_eur > 0).
  totals?: {
    today_kwh: number | null;
    month_kwh: number | null;
    year_kwh: number | null;
    today_eur?: number | null;
    month_eur?: number | null;
    year_eur?: number | null;
    price_eur_per_kwh?: number;
  };
}

// Die Aggregate aus der Datenbank; die Geräte stehen im Steckdosen-Stand.
let _spark = $state<EnergySummary["spark"]>(undefined);
let _totals = $state<EnergySummary["totals"]>(undefined);
let _started = false;
let _sparkTimer: ReturnType<typeof setInterval> | undefined;
let _totalsTimer: ReturnType<typeof setInterval> | undefined;
let _unsub: Array<() => void> = [];

// Die Zusammenfassung: Geräte aus dem Steckdosen-Stand, Summe und Zahl der
// Meldenden daraus. Vorhanden erst, wenn die Geräte bekannt sind -- sonst
// zeigten Karte und Seite „0 W“ statt „lädt“, solange nur Aggregate da sind.
const _summary = $derived.by((): EnergySummary | undefined => {
  const liste: EnergyDevice[] | undefined = getEnergieGeraete();
  if (liste === undefined) return undefined;
  return {
    devices: liste,
    total_watts: liste.reduce((s, d) => s + (d.watts ?? 0), 0),
    reporting: liste.filter((d) => d.watts != null).length,
    spark: _spark,
    totals: _totals,
  };
});

function persistAggregate(): void {
  persist(STORAGE_KEY, { spark: $state.snapshot(_spark), totals: $state.snapshot(_totals) });
}

// Spark-Verlauf wird seltener geholt als die Live-Watts: bei hours=48 rechnet
// der Server 30-min-Buckets (bucketExprForHours), davon bewegt sich nur der
// letzte -- in einer 28 px hohen Kurve aus 96 Punkten unsichtbar. Initial
// sofort, dann alle 5 min (vorher jede Minute, ~5 kB/min je Tab).
const SPARK_TAKT_MS = 5 * 60_000;
async function refreshSpark(): Promise<void> {
  try {
    const r = await fetch("/v2-api/energy/history?hours=48", {
      credentials: "include",
    });
    if (!r.ok) return;
    const rows = (await r.json()) as { bucket: string; watts: number | null }[];
    if (rows.length === 0) return;
    _spark = { values: rows.map((p) => p.watts) };
    persistAggregate();
  } catch {
    // ignore -- naechster Tick versucht's wieder
  }
}

// Totals-Endpoint liefert kWh fuer heute/Monat/Jahr. Polling 60 s --
// kWh-Werte aendern sich gemaechlich.
async function refreshTotals(): Promise<void> {
  try {
    const r = await fetch("/v2-api/energy/totals", { credentials: "include" });
    if (!r.ok) return;
    _totals = (await r.json()) as NonNullable<EnergySummary["totals"]>;
    persistAggregate();
  } catch {
    // ignore -- naechster Tick versucht's wieder
  }
}

export function startEnergySummary(): void {
  if (_started) return;
  _started = true;
  // Die Geräte holt der Steckdosen-Stand selbst (Start, Revalidator, Neuversuch).
  _unsub.push(startSteckdosen());
  _unsub.push(
    registerRevalidator(() => {
      void refreshSpark();
      void refreshTotals();
    }),
  );
  // SWR: gecachte Aggregate sofort einspielen damit die Dashboard-Karte beim
  // Reload nicht erst "—" zeigt, waehrend die Endpoints laufen. (Frueher lag
  // hier die ganze Zusammenfassung; auch dieses Format traegt spark/totals.)
  const cached = loadPersisted<Partial<EnergySummary>>(STORAGE_KEY);
  if (cached) {
    _spark = cached.spark;
    _totals = cached.totals;
  }
  void refreshSpark();
  void refreshTotals();
  _sparkTimer = setInterval(refreshSpark, SPARK_TAKT_MS);
  _totalsTimer = setInterval(refreshTotals, 60_000);
}

export function stopEnergySummary(): void {
  _started = false;
  if (_sparkTimer) {
    clearInterval(_sparkTimer);
    _sparkTimer = undefined;
  }
  if (_totalsTimer) {
    clearInterval(_totalsTimer);
    _totalsTimer = undefined;
  }
  for (const off of _unsub) off();
  _unsub = [];
  _spark = undefined;
  _totals = undefined;
}

export function getEnergySummary(): EnergySummary | undefined {
  return _summary;
}
